import React, { useState } from 'react'
import { FormikProvider } from 'formik'

import tempData from './tempData'
import { chargers_config } from './columnConfig'

import handAndMonyIcon from 'assets/images/dollar_au_icon.png'
import co2SvgIcon from 'assets/images/co2_icon.svg'

import { CustomTable } from 'atoms/CustomTable'
import Typography from 'atoms/Typography'
import BarChart from 'atoms/Charts/BarChart'
import useWindowSize from 'hooks/useWindowSize'
import useVehicleResult from './useVehicleResult'
import { capitalize } from 'lodash'

import {
  StyledRoot,
  StyledHeader,
  StyledHeaderButton,
  StyledTotalBox,
  StyledTotalContainer,
  StyledTotalIcon,
  StyledTotalText,
  StyledRangeContainer,
  StyledRangeWrapper,
  StyledTitle,
  StyledRangeBox,
  StyledVerticalLine,
  StyledChartContainer,
  StyledVerticalTypography,
  StyledChargeContainer,
  StyledScrollContainer,
  StyledDisclaimerContainer,
  StyledResultBoxText,
  StyledInnerFormContainer,
  StyledCustomSelect,
} from './VehicleResultsStyle'

import ResponsiveTable from 'components/CustomTable/ResponsiveTable'
import useLastUpdate from 'hooks/useLastUpdate'

const VehicleResults = ({
  total_cost_saving,
  total_emissions_reduced,
  inputData,
  diesel_vehicle,
  chartData,
  co2Chart,
  range_data,
}: any) => {
  const {
    diesel_depreciation,
    electric_depreciation,
    diesel_energy_cost,
    electric_energy_cost,
    diesel_maintenance,
    electric_maintenance,
    battery_replacement,
  } = chartData

  const { diesel_vehicle_range, ev_vehicle_range } = range_data

  const { co2_diesel, co2_electric } = co2Chart

  const { make, model } = inputData

  const { updated_date } = useLastUpdate()

  const datasets = [
    {
      data: [diesel_depreciation, electric_depreciation],
      backgroundColor: '#19B3FF',
      label: 'Depreciated vehicle ',
    },
    {
      data: [diesel_energy_cost, electric_energy_cost],
      backgroundColor: '#FF8D00',
      label: 'Energy cost',
    },
    {
      data: [diesel_maintenance, electric_maintenance],
      backgroundColor: '#96C90A',
      label: 'Maintenance cost',
    },
    {
      data: [null, battery_replacement],
      backgroundColor: '#700036',
      label: 'Battery replacements',
    },
  ]

  const co2DataSet = [
    {
      data: [co2_diesel, co2_electric],
      backgroundColor: '#022B12',
      label: `CO2 emissions `,
    },
    // {
    //   data: [null, co2_electric],
    //   backgroundColor: '#022B12',
    //   label: '',
    // },
  ]

  const labels = [`${diesel_vehicle?.make}`, `${make} ${model}`]

  const [tab, setTab] = useState('disclaimer')
  const config = chargers_config()
  const tempStaticData = tempData()

  const size = useWindowSize()

  const legendPosition = size.width !== undefined && size.width < 768 ? 'bottom' : 'right'

  const { formik, hvVehicleWeightClass, vehicle_models_data, hvAvailabilityOptions, hvVehicleTypeOptions } = useVehicleResult()
  return (
    <StyledRoot>
      <StyledHeader>
        <StyledHeaderButton
          isReview={tab === 'disclaimer'}
          onClick={() => setTab('disclaimer')}
          color=""
        >
          DISCLAIMER
        </StyledHeaderButton>
        <StyledHeaderButton
          isReview={tab === 'overview'}
          onClick={() => setTab('overview')}
          color=""
        >
          RESULTS
        </StyledHeaderButton>
        <StyledHeaderButton isReview={tab === 'charge'} onClick={() => setTab('charge')} color="">
          CHARGE
        </StyledHeaderButton>
        <StyledHeaderButton
          isReview={tab === 'vehicle_models'}
          onClick={() => setTab('vehicle_models')}
          color=""
        >
          Vehicle models
        </StyledHeaderButton>
      </StyledHeader>
      {tab === 'disclaimer' && (
        <StyledDisclaimerContainer>
          <Typography
            style={{ fontSize: 16 }}
            variant="caption"
            as="p"
            weight={400}
            color="#000000"
          >
            <p>
              The data contained in this Heavy Vehicle Comparison Tool has been collated by Evenergi
              Pty Ltd (Evenergi) and is to serve as a tool to guide users in understanding total
              cost of ownership and emissions associated with the transition to heavy low and zero
              emission vehicle technologies.
            </p>

            <p>
              The tool is not a source for financial advice and is only meant as a guide to start
              the conversation on costs and emissions of heavy low and zero emission vehicle
              technologies in your organisation.
            </p>
            <p>
              The Heavy Vehicle Comparison Tool uses calculations based on commercially available heavy
              vehicles. The content of this tool was last updated on: {updated_date}
            </p>
            <p>
              While all care has been taken in compiling the data, no representation is made, and no
              warranty is given as to the accuracy, completeness, adequacy or suitability of any
              data contained herein.
            </p>
            <p>
              Transport for NSW and Evenergi will in no way be responsible for or liable in respect
              of any claim arising out of or in connection with any person’s use of or reliance on
              the data or any purposes for which the data may be put.
            </p>
          </Typography>
        </StyledDisclaimerContainer>
      )}
      {tab === 'overview' && (
        <>
          <StyledTotalContainer>
            <StyledTotalBox>
              <StyledTotalIcon>
                <img src={handAndMonyIcon} alt="hand-and-money" />
                <StyledTotalText color="#000" variant="h3">
                  Total cost savings
                </StyledTotalText>
              </StyledTotalIcon>

              <StyledResultBoxText color="#000" variant="h2">
                A$ {total_cost_saving.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </StyledResultBoxText>
            </StyledTotalBox>

            <StyledTotalBox>
              <StyledTotalIcon>
                <img src={co2SvgIcon} alt="hand-and-money" />
                <StyledTotalText color="#000" variant="h3">
                  Total emissions reduced
                </StyledTotalText>
              </StyledTotalIcon>

              <StyledResultBoxText color="#000" variant="h2">
                {(total_emissions_reduced / 1000000).toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}{' '}
                tonnes
              </StyledResultBoxText>
            </StyledTotalBox>
          </StyledTotalContainer>

          <StyledRangeContainer>
            <StyledTitle mt={30} mb={30} color="#000" variant="h3" weight={600}>
              Range analysis
            </StyledTitle>

            <StyledRangeWrapper>
              <StyledRangeBox>
                <Typography mt={20} color="#000" variant="label">
                  Daily distance travelled
                </Typography>
                <Typography color="#000" variant="h2">
                  {inputData?.distance_travelled_per_day} km
                </Typography>
                <StyledVerticalLine />
              </StyledRangeBox>

              <StyledRangeBox>
                <Typography mt={20} color="#000" variant="label">
                  {inputData?.make} {inputData?.model} range
                </Typography>
                {ev_vehicle_range <= 0 ? (
                  <Typography mt={25} color="#000" variant="label">
                    Range not available
                  </Typography>
                ) : (
                  <Typography color="#000" variant="h2">
                    {ev_vehicle_range} km
                  </Typography>
                )}
                {/* {inputData?.charge_time <= 0 ? (
                  <Typography mt={25} color="#000" variant="label">
                    Charging time not available
                  </Typography>
                ) : (
                  <>
                    <Typography mt={25} color="#000" variant="label">
                      ** Estimated charging time
                    </Typography>
                    <Typography mt={5} color="#000" variant="label">
                      {timeConvert(inputData?.charge_time)}
                    </Typography>
                    <Typography mt={5} color="#000" variant="label">
                      (on a {inputData?.charging} kW charger)
                    </Typography>
                  </>
                )} */}
                <StyledVerticalLine />
              </StyledRangeBox>

              <StyledRangeBox>
                <Typography mt={20} color="#000" variant="label">
                  {capitalize(`${diesel_vehicle?.make} vehicle range`)}
                </Typography>
                <Typography color="#000" variant="h2">
                  {diesel_vehicle_range} km
                </Typography>
              </StyledRangeBox>
            </StyledRangeWrapper>
          </StyledRangeContainer>

          <StyledRangeContainer>
            <StyledTitle mt={30} mb={50} color="#000" variant="h3" weight={600}>
              Cost comparison
            </StyledTitle>
            <StyledVerticalTypography color="#666666" variant="label">
              Total cost (thousands of dollars)
            </StyledVerticalTypography>
            <StyledChartContainer>
              <BarChart
                datasets={datasets ?? []}
                labels={labels ?? []}
                barThickness={60}
                legendPosition={legendPosition}
                alignLegend="start"
              />
            </StyledChartContainer>
          </StyledRangeContainer>
          <StyledRangeContainer paddingRight={72}>
            <StyledTitle mt={30} mb={50} color="#000" variant="h3" weight={600}>
              CO2 emissions comparison
            </StyledTitle>
            <StyledVerticalTypography color="#666666" variant="label">
              CO2 emissions over asset lifetime (in tonnes)
            </StyledVerticalTypography>
            {/* <StyleChartLabel>
              <div className="color_container" />
              <span>CO2 emissions over asset lifetime</span>
            </StyleChartLabel> */}
            <StyledChartContainer>
              <BarChart
                datasets={co2DataSet ?? []}
                labels={labels ?? []}
                barThickness={60}
                legendPosition={legendPosition}
                alignLegend="start"
                // showLabelDisplay={false}
                tonnes
              />
            </StyledChartContainer>
          </StyledRangeContainer>
          <Typography variant={'h5'} as={'p'} color={'#e36653'} style={{ fontSize: 14 }} mt={10}>
            The chassis price used for electric and diesel vehicles are representative in nature.
            Actual costs may vary. The costs shown in this tool does not include consumer price indexing.

          </Typography>
        </>
      )}
      {tab === 'charge' && (
        <StyledChargeContainer>
          <Typography variant="h4" weight={600} color="#000000">
            Reference charging speed / time
          </Typography>

          <StyledScrollContainer>
            <CustomTable
              templateColumns="1fr 1fr 1.5fr 1.5fr"
              size="14px"
              displayHeader
              columnsConfig={config}
              data={tempStaticData}
              alignItems="end"
              tableWidth="100%"
              noBorder
              clearTable
              labelColor={'#004B70'}
            />
          </StyledScrollContainer>
          <Typography variant="caption" weight={600} color="#000000">
            * Estimated cost per charge point only includes capital cost of the charger and not the installation
            or manintenance costs. Time to charge is based on a vehicle with 100 kWh battery size
          </Typography>
        </StyledChargeContainer>
      )}
      {tab === 'vehicle_models' && (
        <FormikProvider value={formik}>
          <StyledChargeContainer>
            <StyledInnerFormContainer>
              <StyledCustomSelect
                mandatory
                name="heavy_vehicle_weight_class"
                labelColor="#000"
                placeholder="Weight class"
                options={[{
                  label: 'All', value: undefined,
                }, ...hvVehicleWeightClass]
                }
              />
              <StyledCustomSelect
                mandatory
                name="heavy_vehicle_availability"
                labelColor="#000"
                placeholder="Availability"
                options={[{
                  label: 'All', value: undefined,
                }, ...hvAvailabilityOptions]
                }
              />
              <StyledCustomSelect
                mandatory
                name="heavy_vehicle_type"
                labelColor="#000"
                placeholder="Vehicle Type"
                options={[{
                  label: 'All', value: undefined,
                }, ...hvVehicleTypeOptions]
                }
              />
            </StyledInnerFormContainer>
            <ResponsiveTable data={vehicle_models_data} />
            <Typography variant="label" weight={500} color="#000" mt={40}>
              * Gross combined weight
            </Typography>
          </StyledChargeContainer>
        </FormikProvider>
      )}
    </StyledRoot>
  )
}
export default VehicleResults
